import { useEffect, useState } from "react";
import { Grid } from "@mui/material";

// Components
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import { TPickerName } from "@/Settings/types/SystemSettings/callActivity";

type TResolveTimeOptionsParams = {
	hoursMax?: number;
	hoursInterval?: number;
	minutesMax?: number;
	minutesInterval?: number;
	secondsMax?: number;
	secondsInterval?: number;
};

const resolveInterval = (interval?: number) => {
	return interval ?? 1;
};

const resolveTimeOptions = ({
	hoursMax,
	hoursInterval,
	minutesMax,
	minutesInterval,
	secondsMax,
	secondsInterval,
}: TResolveTimeOptionsParams) => {
	const selectableHours = [];
	const selectableMinutes = [];
	const selectableSeconds = [];

	for (let i = 0; i < (hoursMax || 24) / resolveInterval(hoursInterval); i++) {
		selectableHours.push({ label: String(i * resolveInterval(hoursInterval)) + " hrs", id: i * resolveInterval(hoursInterval) });
	}

	for (let i = 0; i < (minutesMax || 60) / resolveInterval(minutesInterval); i++) {
		selectableMinutes.push({ label: String(i * resolveInterval(minutesInterval)) + " min", id: i * resolveInterval(minutesInterval) });
	}

	for (let i = 0; i < (secondsMax || 60) / resolveInterval(secondsInterval); i++) {
		selectableSeconds.push({ label: String(i * resolveInterval(secondsInterval)) + " sec", id: i * resolveInterval(secondsInterval) });
	}

	return { selectableHours, selectableMinutes, selectableSeconds };
};

type TTimePickerProps = {
	hoursMax?: number;
	hoursInterval?: number;
	minutesMax?: number;
	minutesInterval?: number;
	secondsMax?: number;
	secondsInterval?: number;
	includeHours?: boolean;
	includeMinutes?: boolean;
	includeSeconds?: boolean;
	readOnly?: boolean;
	name: TPickerName;
	initialValue?: {
		hours?: number;
		minutes?: number;
		seconds?: number;
	};
	updateData?: (name: TPickerName, type: string, id: number) => void;
};

const timePickerStyles = {
	maxWidth: 95,
};

const TimePicker = (props: TTimePickerProps) => {
	const {
		initialValue,
		updateData,
		name,
		readOnly,
		includeHours,
		includeMinutes,
		includeSeconds,
		hoursMax,
		hoursInterval,
		minutesMax,
		minutesInterval,
		secondsMax,
		secondsInterval,
	} = props;

	const [hours, setHours] = useState({ label: 0 + " hrs", id: 0 });
	const [minutes, setMinutes] = useState({ label: 0 + " min", id: 0 });
	const [seconds, setSeconds] = useState({ label: 0 + " sec", id: 0 });

	useEffect(() => {
		setHours({ label: (initialValue?.hours ?? 0) + " hrs", id: initialValue?.hours ?? 0 });
		setMinutes({ label: (initialValue?.minutes ?? 0) + " min", id: initialValue?.minutes ?? 0 });
		setSeconds({ label: (initialValue?.seconds ?? 0) + " sec", id: initialValue?.seconds ?? 0 });
	}, [props]);

	const { selectableHours, selectableMinutes, selectableSeconds } = resolveTimeOptions({
		hoursMax,
		hoursInterval,
		minutesMax,
		minutesInterval,
		secondsMax,
		secondsInterval,
	});

	const propagate = (type: "hours" | "minutes" | "seconds", label: { label: string; id: number }) => {
		const typeToSelectable = {
			hours: selectableHours,
			minutes: selectableMinutes,
			seconds: selectableSeconds,
		};

		const id = typeToSelectable[type]?.find((item) => item.label === label.label)?.id ?? 0;

		if (updateData) {
			updateData(name, type, id);
		}
	};

	return (
		<Grid container>
			{includeHours && (
				<Grid item xs={6}>
					<SmartSelector
						showBy={"label"}
						selectBy={"label"}
						options={selectableHours}
						selected={hours.label ? { label: hours.label } : null}
						listBoxProps={{
							sx: { maxHeight: "220px" },
						}}
						disabled={readOnly}
						sx={timePickerStyles}
						onChangeAction={(label) => propagate("hours", label as { label: string; id: number })}
					/>
				</Grid>
			)}
			{includeMinutes && (
				<Grid item xs={6}>
					<SmartSelector
						showBy={"label"}
						selectBy={"label"}
						options={selectableMinutes}
						selected={minutes.label ? { label: minutes.label } : null}
						listBoxProps={{
							sx: { maxHeight: "220px" },
						}}
						disabled={readOnly}
						sx={timePickerStyles}
						onChangeAction={(label) => propagate("minutes", label as { label: string; id: number })}
					/>
				</Grid>
			)}
			{includeSeconds && (
				<Grid item xs={6}>
					<SmartSelector
						showBy={"label"}
						selectBy={"label"}
						options={selectableSeconds}
						selected={seconds.label ? { label: seconds.label } : null}
						listBoxProps={{
							sx: { maxHeight: "220px" },
						}}
						disabled={readOnly}
						sx={timePickerStyles}
						onChangeAction={(label) => propagate("seconds", label as { label: string; id: number })}
					/>
				</Grid>
			)}
		</Grid>
	);
};

export default TimePicker;
